import { makeAutoObservable, runInAction } from 'mobx'
import dayjs from 'dayjs'
import type { MouseEvent } from 'react'
import type { Blocker } from 'react-router'
import { AxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { DayjsFormats, logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { ZIndex } from 'shared/constants/zIndex'
import modalStore from 'shared/ui/Modal/store/modalStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IParamsOrganizationQuietHours } from 'entities/Organization/api/types'
import { OrganizationApi } from 'entities/Organization'
import { formatTimeTo12Hour, normalizeTime } from 'entities/QuietHours'
import {
  QUIET_HOURS_ALLOWED_TIME_FROM,
  QUIET_HOURS_ALLOWED_TIME_TO,
} from 'entities/QuietHours/lib/constants'
import { RowQuietHours } from '../model/RowQuietHours'

type IPropGetAskIfSaveModal = {
  onToggle: () => void
  blocker?: Blocker
  onProceed?: () => void
}

export class QuietHoursStore {
  private _loading = false
  private _initialQuietHoursSettings: IParamsOrganizationQuietHours | null = null
  private _quietHoursSettings: IParamsOrganizationQuietHours | null = null
  private _initialTime = {
    start: QUIET_HOURS_ALLOWED_TIME_FROM,
    end: QUIET_HOURS_ALLOWED_TIME_TO,
  }
  private readonly _onStateChange?: (isActive: boolean) => void

  private _tableStore = new TableStore<RowQuietHours>({
    key: 'quiet_hours',
  })

  private _idModal = ''

  constructor(private _entities: IEntitiesStore, onStateChange?: (isActive: boolean) => void) {
    makeAutoObservable(this)
    this._onStateChange = onStateChange
  }

  private normalizeQuietHours = (params: IParamsOrganizationQuietHours) => {
    const allowedTimeFrom = normalizeTime(params.allowed_time_from)
    const allowedTimeTo = normalizeTime(params.allowed_time_to)

    return {
      ...params,
      allowed_time_from: allowedTimeFrom,
      allowed_time_to: allowedTimeTo,
    }
  }

  init = () => {
    if (this._entities.organizationStore.quietHoursSettings) {
      const normalized = this.normalizeQuietHours(
        this._entities.organizationStore.quietHoursSettings
      )
      this._quietHoursSettings = { ...normalized }
      this._initialQuietHoursSettings = { ...normalized }
      this._initialTime = {
        start: normalized.allowed_time_from,
        end: normalized.allowed_time_to,
      }
    } else {
      this._quietHoursSettings = {
        is_active: false,
        is_block_on_weekend: false,
        allowed_time_from: this._initialTime.start,
        allowed_time_to: this._initialTime.end,
      }

      this._initialQuietHoursSettings = {
        is_active: false,
        is_block_on_weekend: false,
        allowed_time_from: this._initialTime.start,
        allowed_time_to: this._initialTime.end,
      }
    }
  }

  get quietHoursEnabled() {
    return this._quietHoursSettings?.is_active
  }

  get quietHoursBlockOnWeekend() {
    return this._quietHoursSettings?.is_block_on_weekend ?? false
  }

  get violatesFederalLawRecommendation() {
    const resumeTime = this._initialTime.start
    const stopTime = this._initialTime.end
    const resumeTotalMinutesMax = 480
    const stopTotalMinutesMin = 1260

    const normalizedResume = normalizeTime(resumeTime)
    const normalizedStop = normalizeTime(stopTime)

    const [resumeHours, resumeMinutes] = normalizedResume.split(':').map(Number)
    const [stopHours, stopMinutes] = normalizedStop.split(':').map(Number)

    const resumeTotalMinutes = resumeHours * 60 + resumeMinutes
    const stopTotalMinutes = stopHours * 60 + stopMinutes

    return resumeTotalMinutes < resumeTotalMinutesMax || stopTotalMinutes > stopTotalMinutesMin
  }

  get loading() {
    return this._loading
  }

  private get _quietHoursChangeFlags(): {
    isActiveChanged: boolean
    isBlockOnWeekendChanged: boolean
    timeChanged: boolean
  } {
    if (!this._quietHoursSettings || !this._initialQuietHoursSettings) {
      return { isActiveChanged: false, isBlockOnWeekendChanged: false, timeChanged: false }
    }
    const isActiveChanged =
      this._quietHoursSettings.is_active !== this._initialQuietHoursSettings.is_active
    const isBlockOnWeekendChanged =
      this._quietHoursSettings.is_block_on_weekend !==
      this._initialQuietHoursSettings.is_block_on_weekend
    const normalizedInitialStart = this._initialQuietHoursSettings.allowed_time_from
    const normalizedCurrentStart = normalizeTime(this._initialTime.start)
    const normalizedInitialEnd = this._initialQuietHoursSettings.allowed_time_to
    const normalizedCurrentEnd = normalizeTime(this._initialTime.end)
    const timeChanged =
      normalizedInitialStart !== normalizedCurrentStart ||
      normalizedInitialEnd !== normalizedCurrentEnd
    return { isActiveChanged, isBlockOnWeekendChanged, timeChanged }
  }

  get isShowActions() {
    const { isActiveChanged, isBlockOnWeekendChanged, timeChanged } = this._quietHoursChangeFlags
    return isActiveChanged || isBlockOnWeekendChanged || timeChanged
  }

  private get _hasOnlyActiveStateChanged(): boolean {
    const { isActiveChanged, isBlockOnWeekendChanged, timeChanged } = this._quietHoursChangeFlags
    return isActiveChanged && !isBlockOnWeekendChanged && !timeChanged
  }

  get quietHoursStatusLabel() {
    if (this.isShowActions && !this._hasOnlyActiveStateChanged) return 'saved'
    return this.quietHoursEnabled ? 'activated' : 'disabled'
  }

  get tableStore() {
    return this._tableStore
  }

  get rows() {
    const rows: RowQuietHours[] = []

    const resumeTime = this._initialTime.start || QUIET_HOURS_ALLOWED_TIME_FROM
    const stopTime = this._initialTime.end || QUIET_HOURS_ALLOWED_TIME_TO
    const resumeTimeFormatted = formatTimeTo12Hour(resumeTime)
    const stopTimeFormatted = formatTimeTo12Hour(stopTime)

    rows.push(
      new RowQuietHours({
        id: 'weekdays',
        days: `Monday - ${this.quietHoursBlockOnWeekend ? 'Friday' : 'Sunday'}`,
        timeRange: `${resumeTimeFormatted} - ${stopTimeFormatted}`,
      })
    )

    if (this.quietHoursBlockOnWeekend) {
      rows.push(
        new RowQuietHours({
          id: 'weekend',
          days: 'Saturday - Sunday',
          timeRange: 'Blocked',
        })
      )
    }

    return rows
  }

  getDate = (timeString: string) => {
    const today = uiStore.dayjsWithoutTimezone().format(DayjsFormats.dateSort)
    return dayjs(`${today} ${timeString}`, DayjsFormats.apiFormat3).toDate()
  }

  getTime = (field: 'start' | 'end') => {
    const timeString = normalizeTime(this._initialTime[field] || QUIET_HOURS_ALLOWED_TIME_TO)
    return this.getDate(timeString)
  }

  setTime = (date: Date | null, field: 'start' | 'end') => {
    if (!date) return

    this._initialTime[field] = normalizeTime(dayjs(date).format(DayjsFormats.time4))

    this.updateAllowedTime()
  }

  toggleActive = async (status: boolean) => {
    if (!this._quietHoursSettings || this._loading) return

    this._quietHoursSettings = {
      is_active: status,
      is_block_on_weekend: this._quietHoursSettings.is_block_on_weekend,
      allowed_time_from: this._quietHoursSettings.allowed_time_from,
      allowed_time_to: this._quietHoursSettings.allowed_time_to,
    }
  }

  changeIsBlockOnWeekend = async (status: boolean) => {
    if (!this._quietHoursSettings) return

    this._quietHoursSettings = {
      is_active: this._quietHoursSettings.is_active,
      is_block_on_weekend: status,
      allowed_time_from: this._quietHoursSettings.allowed_time_from,
      allowed_time_to: this._quietHoursSettings.allowed_time_to,
    }
  }

  updateAllowedTime = () => {
    if (!this._quietHoursSettings) return

    this._quietHoursSettings = {
      is_active: this._quietHoursSettings.is_active,
      is_block_on_weekend: this._quietHoursSettings.is_block_on_weekend,
      allowed_time_from: normalizeTime(this._initialTime.start),
      allowed_time_to: normalizeTime(this._initialTime.end),
    }
  }

  handleCancel = () => {
    if (this._initialQuietHoursSettings) {
      this._quietHoursSettings = {
        is_active: this._initialQuietHoursSettings.is_active,
        is_block_on_weekend: this._initialQuietHoursSettings.is_block_on_weekend,
        allowed_time_from: this._initialQuietHoursSettings.allowed_time_from,
        allowed_time_to: this._initialQuietHoursSettings.allowed_time_to,
      }
      this._initialTime = {
        start: normalizeTime(this._initialQuietHoursSettings.allowed_time_from),
        end: normalizeTime(this._initialQuietHoursSettings.allowed_time_to),
      }

      this._onStateChange?.(this._initialQuietHoursSettings.is_active)
    }
  }

  saveSettings = async (): Promise<boolean> => {
    if (this._loading) return false
    if (!this._quietHoursSettings || !this.isShowActions) return false

    runInAction(() => {
      this._loading = true
    })

    try {
      await OrganizationApi.updateOrganizationQuietHours(this._quietHoursSettings)
      this._entities.organizationStore.updateOrganizationQuietHoursSettings(
        this._quietHoursSettings
      )

      const statusLabel = this.quietHoursStatusLabel
      this._initialQuietHoursSettings = { ...this._quietHoursSettings }

      toastStore.add({
        type: 'success',
        title: `Quiet hours ${statusLabel}`,
      })
      return true
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        toastStore.add({
          type: 'error',
          title: e.errors.getError('message') || e.errors.getError('default'),
        })
      }

      return false
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  getAskIfSaveModal = ({ onToggle, blocker, onProceed }: IPropGetAskIfSaveModal) => {
    this._idModal = 'askSaveQuietHours_modal'

    return {
      disabledOnAllClose: true,
      type: ModalTypeList.WARNING,
      title: 'Save changes before leaving?',
      id: this._idModal,
      zIndex: ZIndex.OVERLAY_MIDDLE,
      primaryAction: {
        text: 'Yes, save',
        disabled: this._loading,
        onAction: async (e?: MouseEvent<HTMLButtonElement>) => {
          e?.stopPropagation()
          const saved = await this.saveSettings()
          if (!saved) return
          modalStore.closeModal(this._idModal)
          onToggle()
          blocker?.proceed?.()
          onProceed?.()
        },
      },
      additionalSecondaryAction: {
        text: 'Don’t save',
        disabled: this._loading,
        onAction: (e?: MouseEvent<HTMLButtonElement>) => {
          e?.stopPropagation()
          this.handleCancel()
          onToggle()
          modalStore.closeModal(this._idModal)
          blocker?.proceed?.()
          onProceed?.()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        disabled: this._loading,
        onAction: (e?: MouseEvent<HTMLButtonElement>) => {
          e?.stopPropagation()
          modalStore.closeModal(this._idModal)
          blocker?.reset?.()
        },
      },
    }
  }

  openAskIfSaveModal = (props: IPropGetAskIfSaveModal) => {
    modalStore.addModal(this.getAskIfSaveModal(props))
  }
}
